/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { baseApiService } from '../api'
import { getCurrentLang } from '../auth'

type TextsMap = Record<string, string>
type Language = { id: string; name: string }
interface TextType {
  id?: string
  active?: boolean
  localeTag?: string
  englishName?: string
  nativeName?: string
  value?: string
  msgs?: {
    [key: string]: {
      value?: string | null
      id?: number | null
    }
  }
  moveToPosition?: number
  position?: number
  items?: Array<ItemType>
  editingMode?: boolean
  canDelete?: boolean
  locale?: string
  text?: string
}
interface ItemType {
  id?: number | null
  locale?: string
  text?: string
}
interface Message {
  id: number
  messageType: string
  value: string
  locale: string
}
interface FindMessageResponse {
  elements: Message[]
  totalCount: number
}
interface ParamType {
  sortBy: string
  sortDesc: boolean
}

export function textsFactory() {
  let textsRef: TextsMap = {}
  let possibleLanguagesRef: Language[] = []
  let assembledMsgsObj: TextType[] = []
  let assembledJustificationsObj: TextType[] = []
  let assembledRejectionReasonsObj: TextType[] = []
  let assembledCrTaskTeamStepLabelsObj: TextType[] = []

  // Helper: string interpolation
  function _sprintf(text: string, params?: Record<string, string>) {
    let formatted = text
    params = params || {}
    if (typeof formatted === 'string') {
      Object.keys(params).forEach((key) => {
        const regexp = new RegExp(`\\{{${key}\\}}`, 'gi')
        formatted = formatted.replace(regexp, params[key])
      })
    }
    return formatted
  }

  // Download all texts and cache in localStorage
  async function downloadTexts(): Promise<TextsMap> {
    const data = await baseApiService.fetch<Message[]>('/api/messages', {
      method: 'GET',
      params: { sortBy: 'position', sortDesc: false }
    })
    const texts: TextsMap = {}
    data.forEach((item) => {
      texts[item.messageType] = item.value
    })
    textsRef = texts
    localStorage.setItem('texts', JSON.stringify(texts))
    return texts
  }

  // Get all texts from cache
  function getAllTexts(): TextsMap {
    if (!Object.keys(textsRef).length) {
      const cached = localStorage.getItem('texts')
      if (cached) textsRef = JSON.parse(cached)
    }
    return textsRef
  }

  // Get a single text, with interpolation
  function getText(key: string, params?: Record<string, string>): string {
    const texts = getAllTexts()
    if (typeof texts[key] !== 'undefined') {
      return _sprintf(texts[key], params)
    } else {
      // Try to reload if missing
      downloadTexts()
      return '?'
    }
  }

  // API: Download texts (raw)
  async function apiDownloadTexts(params?: ParamType) {
    return baseApiService.fetch<Message[]>('/api/messages', {
      method: 'GET',
      params: { ...params }
    })
  }

  // Assemble esignature messages for all languages
  async function assembleEsignatureMsgs(languages?: Language[]) {
    // Initialize assembledMsgsObj with languages if provided and array is empty
    if (languages && assembledMsgsObj.length === 0) {
      assembledMsgsObj = languages.map((lang) => ({ ...lang, msgs: {} }))
    }

    const data = await baseApiService.fetch<FindMessageResponse>('/api/messages/find')
    const allMsgs = data.elements

    assembledMsgsObj.forEach((langData) => {
      const lang = langData
      if (!lang.msgs) lang.msgs = {}
      const msgs = allMsgs.filter((item) => item.locale === lang.id)
      msgs.forEach((msg) => {
        if (lang.msgs) {
          lang.msgs[msg.messageType] = { value: msg.value }
        }
      })
      // Fill missing message types with null
      Object.keys(allMsgs[0]?.messageType || {}).forEach((messageType) => {
        if (lang?.msgs) {
          if (!lang.msgs[messageType]) {
            lang.msgs[messageType] = { value: null }
          }
        }
      })
    })
    return assembledMsgsObj
  }

  // Save assembled esignature messages
  async function saveAssembledEsignatureMsgs(assembledMsgsRef = assembledMsgsObj) {
    const messages: { locale: string; messageType: string; value: string }[] = []
    const copy = JSON.parse(JSON.stringify(assembledMsgsRef))
    copy.forEach((item: TextType) => {
      for (const key in item.msgs) {
        if (item.msgs[key].value) {
          messages.push({ locale: item.id ?? '', messageType: key, value: item.msgs[key].value })
        }
      }
    })
    await baseApiService.fetch('/api/messages', {
      method: 'PUT',
      body: { messages }
    })
    await downloadTexts()
    return true
  }

  // Assemble justifications (change)
  async function assembleJustifications(languages?: Language[]) {
    // Update possibleLanguagesRef if provided
    if (languages && languages.length > 0) {
      possibleLanguagesRef = languages
    }

    const data = await baseApiService.fetch<TextType[]>('/api/justifications/all', {
      method: 'GET',
      params: { type: 'JUSTIFICATION_CHANGE' }
    })
    assembledJustificationsObj = data
    assembledJustificationsObj.forEach((position) => {
      if (!position.msgs) position.msgs = {}
      position.moveToPosition = position.position
      possibleLanguagesRef.forEach((lng) => {
        const value = position?.items?.find((item) => item.locale === lng.id)
        if (position?.msgs) {
          position.msgs[lng.id] = { value: value ? value.text : null, id: value ? value.id : null }
        }
      })
    })
    return assembledJustificationsObj
  }

  // Save assembled justifications (change)
  async function saveAssembledJustifications(assembledJustificationsRef = assembledJustificationsObj) {
    const reqObj: { justifications: TextType[]; type: string } = { justifications: [], type: 'JUSTIFICATION_CHANGE' }
    const copy = JSON.parse(JSON.stringify(assembledJustificationsRef))
    let newPosition = 0
    copy.forEach((item: TextType) => {
      const positionRow = { position: item.position, moveToPosition: newPosition, items: [] as ItemType[] }
      for (const key in item.msgs) {
        positionRow.items.push({ locale: key, text: item.msgs[key].value ?? '', id: item.msgs[key].id })
      }
      reqObj.justifications.push(positionRow)
      newPosition++
    })
    await baseApiService.fetch('/api/justifications', {
      method: 'PUT',
      body: reqObj
    })
    return true
  }

  // Assemble rejection reasons
  async function assembleRejectionReasons(languages?: Language[]) {
    // Update possibleLanguagesRef if provided
    if (languages && languages.length > 0) {
      possibleLanguagesRef = languages
    }

    const data = await baseApiService.fetch('/api/justifications/all', {
      method: 'GET',
      params: { type: 'JUSTIFICATION_REJECT' }
    })
    assembledRejectionReasonsObj = data as TextType[]
    assembledRejectionReasonsObj.forEach((position) => {
      if (!position.msgs) position.msgs = {}
      position.moveToPosition = position.position
      possibleLanguagesRef.forEach((lng) => {
        const value = position?.items?.find((item: ItemType) => item.locale === lng.id)
        if (position.msgs) {
          position.msgs[lng.id] = { value: value ? value.text : null, id: value ? value.id : null }
        }
      })
    })
    return assembledRejectionReasonsObj
  }

  // Save assembled rejection reasons
  async function saveAssembledRejectionReasons(assembledRejectionReasonsRef = assembledRejectionReasonsObj) {
    const reqObj: { justifications: TextType[]; type: string } = { justifications: [], type: 'JUSTIFICATION_REJECT' }
    const copy = JSON.parse(JSON.stringify(assembledRejectionReasonsRef))
    let newPosition = 0
    copy.forEach((item: TextType) => {
      const positionRow = { position: item.position, moveToPosition: newPosition, items: [] as ItemType[] }
      for (const key in item.msgs) {
        positionRow.items.push({ locale: key, text: item.msgs[key].value ?? '', id: item.msgs[key].id })
      }
      reqObj.justifications.push(positionRow)
      newPosition++
    })
    await baseApiService.fetch('/api/justifications', {
      method: 'PUT',
      body: reqObj
    })
    return true
  }

  // Assemble CR task team step labels
  async function assembleCrTaskTeamStepLabels(languages?: Language[]) {
    // Update possibleLanguagesRef if provided
    if (languages && languages.length > 0) {
      possibleLanguagesRef = languages
    }

    const data = await baseApiService.fetch('/api/v1/taskteamsteplabels/all')
    assembledCrTaskTeamStepLabelsObj = data as TextType[]
    assembledCrTaskTeamStepLabelsObj.forEach((position) => {
      if (!position.msgs) position.msgs = {}
      position.moveToPosition = position.position
      possibleLanguagesRef.forEach((lng) => {
        const value = position?.items?.find((item: ItemType) => item.locale === lng.id)
        if (position.msgs) {
          position.msgs[lng.id] = { value: value ? value.text : null, id: value ? value.id : null }
        }
      })
    })
    return assembledCrTaskTeamStepLabelsObj
  }

  // Save CR task team step labels
  async function saveAssembledCrTaskTeamStepLabels(
    assembledCrTaskTeamStepLabelsRef = assembledCrTaskTeamStepLabelsObj
  ) {
    const reqObj: { taskTeamStepLabels: TextType[] } = { taskTeamStepLabels: [] }
    const copy = JSON.parse(JSON.stringify(assembledCrTaskTeamStepLabelsRef))
    let newPosition = 0
    copy.forEach((item: TextType) => {
      const positionRow = { position: item.position, moveToPosition: newPosition, items: [] as ItemType[] }
      for (const key in item.msgs) {
        positionRow.items.push({ locale: key, text: item.msgs[key].value ?? '', id: item.msgs[key].id })
      }
      reqObj.taskTeamStepLabels.push(positionRow)
      newPosition++
    })
    await baseApiService.fetch('/api/v1/taskteamsteplabels', {
      method: 'PUT',
      body: reqObj
    })
    return true
  }

  // Add row helpers
  function addJustificationRow() {
    const arr = assembledJustificationsObj
    const last = arr[arr.length - 1] || { moveToPosition: 0 }
    const newRow: TextType = {
      moveToPosition: last?.moveToPosition ? last.moveToPosition + 1 : 1,
      editingMode: true,
      msgs: {}
    }
    possibleLanguagesRef.forEach((lng) => {
      if (newRow.msgs) {
        newRow.msgs[lng.id] = { value: null, id: null }
      }
    })
    arr.push(newRow)
  }

  function addCrTaskTeamStepLabelRow() {
    const arr = assembledCrTaskTeamStepLabelsObj
    const last = arr[arr.length - 1] || { moveToPosition: 0 }
    const newRow: TextType = {
      moveToPosition: last?.moveToPosition ? last.moveToPosition + 1 : 1,
      editingMode: true,
      canDelete: true,
      msgs: {}
    }
    possibleLanguagesRef.forEach((lng) => {
      if (newRow.msgs) {
        newRow.msgs[lng.id] = { value: null, id: null }
      }
    })
    arr.push(newRow)
  }

  function addRejectionReasonRow() {
    const arr = assembledRejectionReasonsObj
    const last = arr[arr.length - 1] || { moveToPosition: 0 }
    const newRow: TextType = {
      moveToPosition: last.moveToPosition ? last.moveToPosition + 1 : 1,
      editingMode: true,
      msgs: {}
    }
    possibleLanguagesRef.forEach((lng) => {
      if (newRow.msgs) {
        newRow.msgs[lng.id] = { value: null, id: null }
      }
    })
    arr.push(newRow)
  }

  // Remove row helpers
  function removeRejectionReasonRow(index: number, ref = assembledRejectionReasonsObj) {
    if (index > -1) ref.splice(index, 1)
    return ref
  }

  function removeJustificationRow(index: number, ref = assembledJustificationsObj) {
    if (index > -1) ref.splice(index, 1)
    return ref
  }

  function removeCrTaskTeamStepLabelRow(index: number, ref = assembledCrTaskTeamStepLabelsObj) {
    if (index > -1) ref.splice(index, 1)
    return ref
  }

  // Init: load texts and languages
  async function init() {
    const cached = localStorage.getItem('texts')
    if (cached) textsRef = JSON.parse(cached)
    await downloadTexts()
    // Simulate languageService.getAllowedLanguages()
    // Replace with actual API if available
    possibleLanguagesRef = [{ id: getCurrentLang(), name: getCurrentLang() }]
    assembledMsgsObj = JSON.parse(JSON.stringify(possibleLanguagesRef))
  }

  // Export all functions
  return {
    assembledMsgs: () => assembledMsgsObj,
    assembledJustifications: () => assembledJustificationsObj,
    assembledRejectionReasons: () => assembledRejectionReasonsObj,
    assembledCrTaskTeamStepLabels: () => assembledCrTaskTeamStepLabelsObj,
    saveAssembledCrTaskTeamStepLabels,
    saveAssembledJustifications,
    saveAssembledEsignatureMsgs,
    saveAssembledRejectionReasons,
    downloadTexts,
    getText,
    apiDownloadTexts,
    init,
    assembleCrTaskTeamStepLabels,
    assembleRejectionReasons,
    assembleJustifications,
    assembleEsignatureMsgs,
    addCrTaskTeamStepLabelRow,
    addJustificationRow,
    addRejectionReasonRow,
    removeRejectionReasonRow,
    removeJustificationRow,
    removeCrTaskTeamStepLabelRow,
    getAllTexts
  }
}
